import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { tenantUserInvitationListMineSpec, tenantUserInvitationValidateSpec, type TenantUserInvitationRow, TenantUserRole } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { toastError, toastSuccess } from '@/components/action/Toaster'
import { Button } from '@/components/action/Button'
import { TextBox } from '@/components/scalar/TextBox'
import { toShortDate } from '@/utilities/formatters'
import type { DataGridState } from '@/components/dataGrid/createDataGridState'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'

export function HomeLaunchInvitations() {
  const api = useApi({ requireLogin: true })
  const navigate = useNavigate()

  const [invitations, setInvitations] = useState<TenantUserInvitationRow[]>([])
  const [codeInput, setCodeInput] = useState('')
  const [validating, setValidating] = useState(false)
  const [invitationGridState, setInvitationGridState] = useState<DataGridState<InvitationGridRow>>({
    rows: [],
    totalRows: 0,
    skip: 0,
    take: 10,
  })

  const refreshInvitations = useCallback(() => {
    api.request(tenantUserInvitationListMineSpec).then(res => {
      setInvitations(res.data?.invitations ?? [])
    })
  }, [])

  useEffect(() => {
    if (!api.status.session?.loggedIn) return
    refreshInvitations()
  }, [api.status.session?.loggedIn, refreshInvitations])

  useEffect(() => {
    setInvitationGridState({
      rows: invitations.map((inv, i) => ({
        ...inv,
        _key: `${inv.tenantId}-${inv.id}-${i}`,
      })),
      totalRows: invitations.length,
      skip: 0,
      take: Math.max(invitations.length, 1),
    })
  }, [invitations])

  const handleAcceptInvitation = useCallback(async (row: TenantUserInvitationRow) => {
    if (row.tenantId == null || row.id == null) return
    setValidating(true)
    try {
      const res = await api.request(tenantUserInvitationValidateSpec, {
        path: { tenant: String(row.tenantId), invitation: String(row.id) },
        body: {},
      })
      if (res.status === 200 && res.data?.success) {
        toastSuccess('Invitation accepted')
        refreshInvitations()
        navigate(routes.home(String(row.tenantId)))
      } else {
        toastError(res.data?.expired ? 'Invitation has expired' : (res.error ?? 'Failed to accept'))
      }
    } catch {
      toastError('Failed to accept invitation')
    } finally {
      setValidating(false)
    }
  }, [refreshInvitations, navigate])

  const handleAcceptByCode = useCallback(async () => {
    const trimmed = codeInput.trim()
    const parts = trimmed.split(':')
    if (parts.length !== 3) {
      toastError('Code must be in the form tenantId:invitationId:passcode (e.g. 4:2:361020)')
      return
    }
    const [tenantId, invitationId, passcode] = parts
    if (!tenantId || !invitationId || !passcode) {
      toastError('Code must have three parts separated by colons')
      return
    }
    setValidating(true)
    try {
      const res = await api.request(tenantUserInvitationValidateSpec, {
        path: { tenant: tenantId, invitation: invitationId },
        body: { passcode },
      })
      if (res.status === 200 && res.data?.success) {
        toastSuccess('Invitation accepted')
        setCodeInput('')
        refreshInvitations()
        navigate(routes.home(tenantId))
      } else {
        toastError(res.data?.expired ? 'Invitation has expired' : (res.error ?? 'Invalid or expired code'))
      }
    } catch {
      toastError('Failed to redeem code')
    } finally {
      setValidating(false)
    }
  }, [codeInput, refreshInvitations, navigate])

  const invitationColumns = useMemo<DataGridColumn<InvitationGridRow>[]>(() => [
    {
      id: 'tenantName',
      label: 'Tenant',
    },
    {
      id: 'role',
      label: 'Role',
      render: (v: TenantUserRole | undefined) => (v != null ? TenantUserRole[v] : ''),
    },
    {
      id: 'expiresAt',
      label: 'Expires',
      render: (v: string | undefined) => (v ? toShortDate(v) : ''),
    },
    {
      id: 'message',
      label: 'Message',
      render: v => v ?? '-',
    },
    {
      id: '_key',
      label: '',
      className: () => 'NoWrap',
      render: (_v, { row }) => (
        <Button
          label='Accept'
          icon='check'
          disabled={validating}
          onClick={() => void handleAcceptInvitation(row)}
        />
      ),
    },
  ], [validating, handleAcceptInvitation])

  return (
    <div className='Cutout Wrapped'>
      <h2>Tenant invitations</h2>

      {invitations.length > 0 && (
        <>
          <br />
          <DataGrid<InvitationGridRow>
            state={invitationGridState}
            setState={setInvitationGridState}
            idColumn='_key'
            columns={invitationColumns}
          />
          <br />
        </>
      )}

      <h3>Have an invitation code?</h3>
      <TextBox
        placeholder='0:0:000000'
        width={240}
        value={codeInput}
        setValue={setCodeInput}
      />
      <Button
        label='Accept invitation'
        icon='unlock'
        disabled={validating || !/^\d+:\d+:\d{6}$/.test(codeInput.trim())}
        onClick={() => void handleAcceptByCode()}
      />
    </div>
  )
}


//--------------------------------------------------------------------------------
type InvitationGridRow = TenantUserInvitationRow & { _key: string }
